import React from 'react';
import { Star, Users } from 'lucide-react';
import { AgentPerformanceMetric } from '../../types/dashboard';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';

interface TopAgentsTableProps {
  agents: AgentPerformanceMetric[];
}

const TopAgentsTable: React.FC<TopAgentsTableProps> = ({ agents }) => {
  if (agents.length === 0) {
    return (
      <Card className="h-full shadow-sm">
        <CardHeader>
          <CardTitle>Top Performing Agents</CardTitle>
          <CardDescription>Last 30 days performance metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[220px] flex-col items-center justify-center gap-3 rounded-lg bg-muted/30">
            <div className="rounded-full bg-background p-3 shadow-sm ring-1 ring-border">
              <Users className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">
                No agent performance data available yet
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground/70">
                Data will appear once agents start handling conversations
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full shadow-sm">
      <CardHeader>
        <CardTitle>Top Performing Agents</CardTitle>
        <CardDescription>Last 30 days · by resolution count</CardDescription>
        <CardAction>
          <Badge variant="outline">Top 5</Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="p-0 pb-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Agent</TableHead>
              <TableHead className="text-right">Convs</TableHead>
              <TableHead className="text-right">Messages</TableHead>
              <TableHead className="text-right">Resolved</TableHead>
              <TableHead className="text-right">CSAT</TableHead>
              <TableHead className="pr-6 text-right">Avg Resp</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => (
              <TableRow key={agent.agent_id}>
                <TableCell className="pl-6">
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={agent.avatar_url} alt={agent.agent_name} />
                      <AvatarFallback className="text-xs font-medium">
                        {agent.agent_name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{agent.agent_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {agent.conversations_handled}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {agent.messages_sent}
                </TableCell>
                <TableCell className="text-right text-sm tabular-nums">
                  {agent.resolutions}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm tabular-nums">
                      {agent.avg_csat_score.toFixed(1)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="pr-6 text-right text-sm tabular-nums text-muted-foreground">
                  {agent.avg_response_time.toFixed(1)} min
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TopAgentsTable;
